import { Pog, type PogNonCircular } from "@CommonTypes/merchflow/pog/pog";

/** Written by the backend when a merchant restriction rule could not be applied to the pog. */
export const RESTRICTION_NOT_APPLIED = "variant_restriction_not_applied";

/** The pogger's never-fail ladder names each rung it took `fallback_<rung>` (DEV-3531). */
const FALLBACK_PREFIX = "fallback_";

/** One rung of the ladder: the rules it relaxed and the error that forced it. */
export interface FallbackRelaxation {
  type: string;
  rules: string[];
  after: string;
}

export type PogWarning =
  | { type: "rangeRecommendation" }
  | { type: "restrictionNotApplied"; rule: string; cdtGroup: string }
  | { type: "relaxedToFinish"; rungs: FallbackRelaxation[] };

/** The rungs the pogger went down to return this pog, in the order it took them. */
export const fallbackRelaxations = (
  pog: Pick<PogNonCircular, "relaxedConstraints"> | null | undefined,
): FallbackRelaxation[] =>
  (pog?.relaxedConstraints ?? [])
    .filter((constraint) => constraint.relaxationType.startsWith(FALLBACK_PREFIX))
    .map((constraint) => {
      const entity = (constraint.entity ?? {}) as { rules?: unknown; after?: unknown };
      return {
        type: constraint.relaxationType,
        rules: Array.isArray(entity.rules) ? entity.rules.map(String) : [],
        after: typeof entity.after === "string" ? entity.after : "",
      };
    });

/** The warnings a pog card shows; an empty array means there is nothing to warn about. */
export const pogWarning = (pog: PogNonCircular | null | undefined): PogWarning[] => {
  if (!pog) return [];
  const warnings: PogWarning[] = [];
  if (new Pog(pog).isHavingRangeRecommendations()) {
    warnings.push({ type: "rangeRecommendation" });
  }
  for (const constraint of pog.relaxedConstraints ?? []) {
    if (constraint.relaxationType !== RESTRICTION_NOT_APPLIED) continue;
    warnings.push({
      type: "restrictionNotApplied",
      rule: String(constraint.entity),
      cdtGroup: String(constraint.magnitude),
    });
  }
  const rungs = fallbackRelaxations(pog);
  if (rungs.length > 0) warnings.push({ type: "relaxedToFinish", rungs });
  return warnings;
};
